import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Trash2, X, Loader2, RefreshCw,
  ChevronDown, ChevronUp, ClipboardList, School,
  Archive, Eye, FileText, Search,
} from 'lucide-react'
import { examsApi, examSessionsApi, classesApi, subjectsApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'
import toast from 'react-hot-toast'

// ── Academic year utils ────────────────────────────────────────────────────────

const ACADEMIC_YEAR_START_MONTH = 3

const formatAcademicYear = (value) => {
  if (!value) return ''
  const s = String(value).trim().replace(/[–—−]/g, '-').replace(/\s+/g, '').replace(/\//g, '-')
  const m = /^(\d{4})-(\d{2}|\d{4})$/.exec(s)
  if (!m) return String(value).trim()
  const startYear = Number(m[1])
  let endYear = Number(m[2])
  if (m[2].length === 2) { const base = Math.floor(startYear / 100) * 100; endYear = base + endYear; if (endYear < startYear) endYear += 100 }
  if (endYear !== startYear + 1) return String(value).trim()
  return `${startYear}-${endYear}`
}

const getCurrentAcademicYear = () => {
  const now = new Date()
  const year = now.getMonth() >= ACADEMIC_YEAR_START_MONTH ? now.getFullYear() : now.getFullYear() - 1
  return `${year}-${year + 1}`
}

// ── Session sort / filter utils ────────────────────────────────────────────────

const toTimestamp = (value) => { if (!value) return 0; const p = new Date(value); return Number.isNaN(p.getTime()) ? 0 : p.getTime() }
const SESSION_STATUS_PRIORITY = { ongoing: 0, scheduled: 1, draft: 2, completed: 3, published: 4 }

const sortExamSessionsForUi = (sessions, { selectedSessionId = '' } = {}) => {
  const currentYear = getCurrentAcademicYear()
  return [...sessions].sort((l, r) => {
    const lId = String(l?._id || l?.id || ''), rId = String(r?._id || r?.id || '')
    const lSel = selectedSessionId && lId === selectedSessionId, rSel = selectedSessionId && rId === selectedSessionId
    if (lSel !== rSel) return lSel ? -1 : 1
    const lCurr = formatAcademicYear(l?.academicYear) === currentYear, rCurr = formatAcademicYear(r?.academicYear) === currentYear
    if (lCurr !== rCurr) return lCurr ? -1 : 1
    const lp = SESSION_STATUS_PRIORITY[String(l?.status || '').toLowerCase()] ?? 99
    const rp = SESSION_STATUS_PRIORITY[String(r?.status || '').toLowerCase()] ?? 99
    if (lp !== rp) return lp - rp
    const td = Math.max(toTimestamp(r?.startDate), toTimestamp(r?.endDate)) - Math.max(toTimestamp(l?.startDate), toTimestamp(l?.endDate))
    if (td !== 0) return td
    return String(l?.name || '').localeCompare(String(r?.name || ''))
  })
}

const countOlderExamSessions = (sessions) => {
  const currentYear = getCurrentAcademicYear()
  return sessions.filter((s) => formatAcademicYear(s?.academicYear) !== currentYear).length
}

const getVisibleExamSessions = (sessions, { includePastYears = false, selectedSessionId = '' } = {}) => {
  const currentYear = getCurrentAcademicYear()
  return sessions.filter((s) =>
    includePastYears || formatAcademicYear(s?.academicYear) === currentYear || String(s?._id || s?.id || '') === selectedSessionId
  )
}

const isExamInCurrentAcademicYearSession = (exam) => {
  const session = exam?.examSessionId
  if (!session || typeof session !== 'object') return false
  return formatAcademicYear(session?.academicYear) === getCurrentAcademicYear()
}

// ── Exam grouping utils ────────────────────────────────────────────────────────

const getExamSessionRecord = (exam) => exam?.examSessionId && typeof exam.examSessionId === 'object' ? exam.examSessionId : {}
const getExamSessionTitle  = (exam) => String(getExamSessionRecord(exam)?.name || '').trim() || 'Other Exams'
const getExamSessionSub    = (exam) => String(getExamSessionRecord(exam)?.academicYear || '').trim()
const getExamClassLabel    = (exam) => `${exam?.classId?.name || ''}${exam?.classId?.section ? ` ${exam.classId.section}` : ''}`.trim() || 'Other Class'

const groupExamsBySessionAndClass = (exams = []) => {
  const sessions = new Map()
  ;[...exams].sort((l, r) => toTimestamp(r?.date) - toTimestamp(l?.date)).forEach((exam) => {
    const session    = getExamSessionRecord(exam)
    const sessionId  = String(session?._id || session?.id || '').trim()
    const sTitle     = getExamSessionTitle(exam)
    const sKey       = sessionId || sTitle
    const sSub       = getExamSessionSub(exam)
    const classId    = String(exam?.classId?._id || exam?.classId?.id || '').trim()
    const cTitle     = getExamClassLabel(exam)
    const cKey       = classId || cTitle
    const sortValue  = toTimestamp(exam?.date)
    const existSess  = sessions.get(sKey)
    if (existSess) {
      existSess.sortValue = Math.max(existSess.sortValue, sortValue)
      existSess.totalCount += 1
      const existClass = existSess.classMap.get(cKey)
      if (existClass) { existClass.sortValue = Math.max(existClass.sortValue, sortValue); existClass.data.push(exam); return }
      existSess.classMap.set(cKey, { key: cKey, classId, title: cTitle, sortValue, data: [exam] })
      return
    }
    sessions.set(sKey, { key: sKey, sessionId, title: sTitle, subtitle: sSub, sortValue, totalCount: 1, classMap: new Map([[cKey, { key: cKey, classId, title: cTitle, sortValue, data: [exam] }]]) })
  })
  return [...sessions.values()].sort((l, r) => r.sortValue - l.sortValue).map((session) => {
    const classGroups = [...session.classMap.values()].sort((l, r) => r.sortValue - l.sortValue).map((group) => ({
      key: `${session.key}-${group.key}`, classId: group.classId, title: group.title,
      subtitle: `${group.data.length} exam${group.data.length === 1 ? '' : 's'}`,
      totalCount: group.data.length, data: group.data,
    }))
    return { key: session.key, sessionId: session.sessionId, title: session.title, subtitle: session.subtitle, totalCount: session.totalCount, classCount: classGroups.length, classGroups }
  })
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) } catch { return dateStr }
}

const parseTimeToMinutes = (value) => {
  const m = /^(\d{2}):(\d{2})$/.exec(value)
  if (!m) return null
  const h = Number(m[1]), min = Number(m[2])
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

const getDuration = (start, end) => {
  const s = parseTimeToMinutes(start), e = parseTimeToMinutes(end)
  if (s === null || e === null || e <= s) return null
  return e - s
}

const EXAM_TYPES = ['theory', 'practical', 'oral', 'project', 'assignment']
const EXAM_TYPE_LABELS = { theory: 'Theory', practical: 'Practical', oral: 'Oral', project: 'Project', assignment: 'Assignment' }
const EXAM_STATUSES = ['draft', 'scheduled', 'ongoing', 'completed', 'published', 'cancelled']
const EXAM_STATUS_LABELS = { draft: 'Draft', scheduled: 'Scheduled', ongoing: 'Ongoing', completed: 'Completed', published: 'Published', cancelled: 'Cancelled' }

// ── ExamCard ───────────────────────────────────────────────────────────────────

function ExamCard({ exam, onResultSheet, onDelete, onStatsChange }) {
  const [sheet, setSheet]     = useState(null)
  const [loading, setLoading] = useState(true)
  const examId                = exam._id || exam.id

  useEffect(() => {
    if (!examId) { setLoading(false); return }
    examsApi.getResultSheet(examId)
      .then((data) => {
        setSheet(data)
        const entries     = data?.entries || data?.rows || []
        const isEntered   = (e) => {
          const st = String(e?.status || '').toLowerCase()
          const at = String(e?.attendanceStatus || '').toLowerCase()
          return (st !== '' && st !== 'pending') || (at !== '' && at !== 'not_entered') || e?.marksObtained !== null
        }
        const isPublished = (e) => String(e?.status || '').toLowerCase() === 'published'
        const total     = Number(data?.totalStudents ?? data?.summary?.totalStudents ?? entries.length) || 0
        const entered   = Number(data?.enteredCount ?? data?.enteredResults ?? data?.summary?.enteredCount ?? data?.summary?.enteredResults ?? entries.filter(isEntered).length) || 0
        const published = Number(data?.publishedCount ?? data?.publishedResults ?? data?.summary?.publishedCount ?? data?.summary?.publishedResults ?? entries.filter(isPublished).length) || 0
        onStatsChange?.(examId, { loaded: true, total, entered, published })
      })
      .catch(() => { onStatsChange?.(examId, { loaded: true, total: 0, entered: 0, published: 0 }) })
      .finally(() => setLoading(false))
  }, [examId]) // eslint-disable-line react-hooks/exhaustive-deps

  const _entries    = sheet?.entries || sheet?.rows || []
  const _isEntered  = (e) => {
    const st = String(e?.status || '').toLowerCase()
    const at = String(e?.attendanceStatus || '').toLowerCase()
    return (st !== '' && st !== 'pending') || (at !== '' && at !== 'not_entered') || e?.marksObtained !== null
  }
  const _isPublished = (e) => String(e?.status || '').toLowerCase() === 'published'
  const total     = Number(sheet?.totalStudents ?? sheet?.summary?.totalStudents ?? _entries.length) || 0
  const entered   = Number(sheet?.enteredCount ?? sheet?.enteredResults ?? sheet?.summary?.enteredCount ?? sheet?.summary?.enteredResults ?? _entries.filter(_isEntered).length) || 0
  const published = Number(sheet?.publishedCount ?? sheet?.publishedResults ?? sheet?.summary?.publishedCount ?? sheet?.summary?.publishedResults ?? _entries.filter(_isPublished).length) || 0
  const allPub      = total > 0 && published >= total
  const isComplete  = total > 0 && entered >= total
  const pending     = Math.max(total - entered, 0)

  const statusColor = allPub ? '#16a34a' : isComplete ? '#4f46e5' : total > 0 ? '#f59e0b' : '#6b7280'
  const statusLabel = allPub ? 'Published' : !total ? 'No students' : isComplete ? 'Ready to publish' : `${pending} pending`

  const subjectName = typeof exam.subjectId === 'object' ? exam.subjectId?.name : ''
  const teacherName = exam.teacherId ? `${exam.teacherId.firstName || ''} ${exam.teacherId.lastName || ''}`.trim() || null : null
  const sessionName = typeof exam.examSessionId === 'object' ? exam.examSessionId?.name : ''
  const examType    = EXAM_TYPE_LABELS[exam.examType] || exam.examType || 'Theory'

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 truncate">{exam.title}</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {subjectName ? `${subjectName} · ` : ''}{getExamClassLabel(exam)}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {formatDate(exam.date)} · {exam.totalMarks} marks · pass {exam.passMarks} · {examType}
          </p>
        </div>
        <button
          onClick={() => onDelete(exam)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-1.5">
        {sessionName && (
          <span className="text-[11px] font-semibold bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">
            {sessionName}
          </span>
        )}
        <span className="text-[11px] font-semibold bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">
          {examType}
        </span>
        {teacherName && (
          <span className="text-[11px] font-semibold bg-cyan-50 text-cyan-700 px-2 py-0.5 rounded-full">
            {teacherName}
          </span>
        )}
      </div>

      {/* Result progress box */}
      <div className="rounded-xl border border-gray-100 bg-gray-50 overflow-hidden">
        {loading ? (
          <div className="h-16 flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
          </div>
        ) : (
          <>
            <div className="flex divide-x divide-gray-200">
              {[
                { label: 'Students', value: total,     color: '' },
                { label: 'Entered',  value: entered,   color: entered > 0 ? 'text-amber-600' : '' },
                { label: 'Published',value: published,  color: allPub ? 'text-emerald-600' : '' },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex-1 text-center py-2.5">
                  <p className={`text-lg font-bold text-gray-900 ${color}`}>{value}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{label}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-1.5 py-1.5 border-t border-gray-200">
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusColor }} />
              <span className="text-[11px] font-semibold" style={{ color: statusColor }}>{statusLabel}</span>
            </div>
          </>
        )}
      </div>

      {/* Result sheet button */}
      <button
        onClick={() => onResultSheet(examId)}
        className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-primary-700 border border-primary-200 bg-primary-50 hover:bg-primary-100 py-2 rounded-xl transition-colors"
      >
        <FileText className="w-3.5 h-3.5" /> View Result Sheet
      </button>
    </div>
  )
}

// ── SessionClassPublishCard ────────────────────────────────────────────────────

function SessionClassPublishCard({ sectionTitle, group, publishing, onResultSheet, onDelete, onPublishGroup }) {
  const [statsByExam, setStatsByExam] = useState({})

  useEffect(() => { setStatsByExam({}) }, [group.key])

  const examIds      = useMemo(() => group.data.map((e) => String(e._id || e.id)), [group.data])
  const groupStats   = examIds.map((id) => statsByExam[id]).filter(Boolean)
  const allLoaded    = examIds.length > 0 && examIds.every((id) => Boolean(statsByExam[id]?.loaded))
  const completeCount   = groupStats.filter((s) => s.total > 0 && s.entered >= s.total).length
  const publishedCount  = groupStats.filter((s) => s.total > 0 && s.published >= s.total).length
  const totalPending    = groupStats.reduce((sum, s) => sum + Math.max(s.total - s.entered, 0), 0)
  const hasMissing      = groupStats.some((s) => s.total === 0)
  const readyToPublish  = allLoaded && !hasMissing && groupStats.length > 0 && groupStats.every((s) => s.total > 0 && s.entered >= s.total)
  const allPublished    = allLoaded && groupStats.length > 0 && groupStats.every((s) => s.total > 0 && s.published >= s.total)

  const statusTone  = allPublished ? '#16a34a' : readyToPublish ? '#4f46e5' : !allLoaded ? '#9ca3af' : '#f59e0b'
  const statusLabel = allPublished ? 'Published' : readyToPublish ? 'Ready to publish' : !allLoaded ? 'Checking...' : 'Pending'
  const helperText  = !allLoaded
    ? 'Checking result sheet progress…'
    : allPublished
      ? 'All results published for this class in this session.'
      : hasMissing
        ? 'Some exams have no students linked. Cannot publish until resolved.'
        : readyToPublish
          ? 'All result sheets are complete. You can publish now.'
          : `${totalPending} result entr${totalPending === 1 ? 'y' : 'ies'} still pending.`

  const handleStats = useCallback((examId, stats) => {
    setStatsByExam((prev) => {
      const curr = prev[examId]
      if (curr && curr.loaded === stats.loaded && curr.total === stats.total && curr.entered === stats.entered && curr.published === stats.published) return prev
      return { ...prev, [examId]: stats }
    })
  }, [])

  return (
    <>
      {/* Publish summary card */}
      <div className="rounded-2xl border border-gray-100 bg-white p-4 space-y-3 mb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">Session Results</p>
            <p className="text-xs text-gray-500 mt-0.5">{sectionTitle} · {group.title}</p>
          </div>
          <span
            className="text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0"
            style={{ backgroundColor: `${statusTone}18`, color: statusTone }}
          >
            {statusLabel}
          </span>
        </div>

        <div className="flex rounded-xl border border-gray-100 bg-gray-50 overflow-hidden">
          <div className="flex-1 text-center py-3">
            <p className="text-lg font-bold text-gray-900">{completeCount}/{examIds.length}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Sheets ready</p>
          </div>
          <div className="w-px bg-gray-200" />
          <div className="flex-1 text-center py-3">
            <p className={`text-lg font-bold ${allPublished ? 'text-emerald-600' : 'text-gray-900'}`}>
              {publishedCount}/{examIds.length}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">Published</p>
          </div>
        </div>

        <p className="text-xs text-gray-500">{helperText}</p>

        <button
          onClick={() => onPublishGroup(group, statsByExam)}
          disabled={!readyToPublish || allPublished || publishing}
          className={`w-full text-sm font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 ${
            allPublished
              ? 'bg-gray-100 text-gray-400 cursor-default'
              : readyToPublish && !publishing
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {publishing && <Loader2 className="w-4 h-4 animate-spin" />}
          {allPublished ? 'Already Published' : 'Publish Session Results'}
        </button>
      </div>

      {/* Exam cards — 2 per row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {group.data.map((exam) => (
          <ExamCard
            key={exam._id || exam.id}
            exam={exam}
            onResultSheet={onResultSheet}
            onDelete={onDelete}
            onStatsChange={handleStats}
          />
        ))}
      </div>
    </>
  )
}

// ── ClassGroupAccordion ────────────────────────────────────────────────────────

function ClassGroupAccordion({ group, expanded, onToggle, children }) {
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden mb-3">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="w-8 h-8 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
          <School className="w-4 h-4 text-primary-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{group.title}</p>
          <p className="text-xs text-gray-400">{group.subtitle}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs font-semibold bg-primary-50 text-primary-700 px-2.5 py-0.5 rounded-full">
            {group.totalCount} exam{group.totalCount === 1 ? '' : 's'}
          </span>
          <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center border border-gray-200">
            {expanded
              ? <ChevronUp className="w-4 h-4 text-primary-600" />
              : <ChevronDown className="w-4 h-4 text-primary-600" />
            }
          </div>
        </div>
      </button>
      {expanded && <div className="p-3">{children}</div>}
    </div>
  )
}

// ── SessionAccordion ───────────────────────────────────────────────────────────

function SessionAccordion({ section, expanded, onToggle, children }) {
  return (
    <div className="card overflow-hidden mb-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-gray-900">{section.title}</p>
          <p className="text-xs text-gray-400 mt-0.5">{section.subtitle}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <ClipboardList className="w-3.5 h-3.5" />
            {section.totalCount} exam{section.totalCount === 1 ? '' : 's'}
          </span>
          <span className="flex items-center gap-1">
            <School className="w-3.5 h-3.5" />
            {section.classCount} class{section.classCount === 1 ? '' : 'es'}
          </span>
          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
            {expanded
              ? <ChevronUp className="w-4 h-4 text-gray-600" />
              : <ChevronDown className="w-4 h-4 text-gray-600" />
            }
          </div>
        </div>
      </button>
      {expanded && <div className="border-t border-gray-100 p-4">{children}</div>}
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  title: '', date: '', startTime: '', endTime: '',
  totalMarks: '', passMarks: '', examType: 'theory', status: 'scheduled',
  subjectId: '', classId: '', examSessionId: '',
  teacherId: '', venue: '', instructions: '',
}

export default function ExamsPage() {
  const navigate            = useNavigate()
  const currentAcademicYear = useMemo(() => getCurrentAcademicYear(), [])
  const [sessions, setSessions] = useState([])
  const [classes, setClasses]   = useState([])
  const [subjects, setSubjects] = useState([])
  const [exams, setExams]       = useState([])
  const [loading, setLoading]   = useState(true)
  const [sessionFilter, setSessionFilter]   = useState('all')
  const [classFilter, setClassFilter]       = useState('all')
  const [showOlderSessions, setShowOlderSessions] = useState(false)
  const [expandedSessionKey, setExpandedSessionKey] = useState(null)
  const [expandedClassBySession, setExpandedClassBySession] = useState({})
  const [publishingGroupKey, setPublishingGroupKey] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing]     = useState(null)
  const [form, setForm]           = useState(EMPTY_FORM)
  const [saving, setSaving]       = useState(false)
  const [allTeachers, setAllTeachers]             = useState([])
  const [formShowOlderSessions, setFormShowOlderSessions] = useState(false)
  const [showTeacherModal, setShowTeacherModal]   = useState(false)
  const [teacherSearch, setTeacherSearch]         = useState('')

  const fetchAll = async () => {
    try {
      setLoading(true)
      const params = {}
      if (sessionFilter !== 'all') params.examSessionId = sessionFilter
      if (classFilter   !== 'all') params.classId       = classFilter
      const [examData, sessionData, classData] = await Promise.allSettled([
        examsApi.getAll(params),
        examSessionsApi.getAll(),
        classesApi.getAll(),
      ])
      setSessions(sessionData.status === 'fulfilled'
        ? (Array.isArray(sessionData.value) ? sessionData.value : sessionData.value?.examSessions ?? sessionData.value?.sessions ?? [])
        : [])
      setClasses(classData.status === 'fulfilled'
        ? (Array.isArray(classData.value) ? classData.value : classData.value?.classes ?? [])
        : [])
      setExams(examData.status === 'fulfilled'
        ? (Array.isArray(examData.value) ? examData.value : examData.value?.exams ?? [])
        : [])
    } catch {
      toast.error('Failed to load exams')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [sessionFilter, classFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.classId) { setSubjects([]); return }
    subjectsApi.getByClass(form.classId)
      .then((data) => setSubjects(Array.isArray(data) ? data : data?.subjects ?? []))
      .catch(() => setSubjects([]))
  }, [form.classId])

  // Load all teachers once when modal opens
  useEffect(() => {
    if (!modalOpen) return
    import('../api/services.js').then(({ teachersApi }) => {
      teachersApi.getAll().then((data) => {
        setAllTeachers(Array.isArray(data) ? data : data?.users ?? [])
      }).catch(() => {})
    })
  }, [modalOpen])

  // ── Derived data ────────────────────────────────────────────────────────────

  const orderedSessions = useMemo(
    () => sortExamSessionsForUi(sessions, { selectedSessionId: sessionFilter !== 'all' ? sessionFilter : '' }),
    [sessions, sessionFilter]
  )
  const visibleSessions = useMemo(
    () => getVisibleExamSessions(orderedSessions, { includePastYears: showOlderSessions, selectedSessionId: sessionFilter !== 'all' ? sessionFilter : '' }),
    [orderedSessions, sessionFilter, showOlderSessions]
  )
  const olderSessionCount = useMemo(() => countOlderExamSessions(orderedSessions), [orderedSessions])
  const sessionOptions    = useMemo(() => [
    { _id: 'all', name: showOlderSessions ? 'All Sessions' : currentAcademicYear },
    ...visibleSessions,
  ], [currentAcademicYear, showOlderSessions, visibleSessions])
  const classOptions      = useMemo(() => [{ _id: 'all', name: 'All Classes' }, ...classes], [classes])

  // Form-specific session chips (use their own show-older state)
  const formVisibleSessions = useMemo(
    () => getVisibleExamSessions(orderedSessions, { includePastYears: formShowOlderSessions, selectedSessionId: form.examSessionId }),
    [orderedSessions, formShowOlderSessions, form.examSessionId]
  )
  const formOlderCount = useMemo(() => countOlderExamSessions(orderedSessions), [orderedSessions])

  // Eligible teachers: teachers assigned to the selected subject
  const selectedSubject = useMemo(
    () => subjects.find((s) => (s._id || s.id) === form.subjectId) || null,
    [subjects, form.subjectId]
  )
  const eligibleTeacherIds = useMemo(() => {
    if (!selectedSubject?.teachers) return []
    return selectedSubject.teachers.map((t) => String(typeof t === 'object' ? (t._id || t.id) : t))
  }, [selectedSubject])
  const eligibleTeachers = useMemo(
    () => allTeachers.filter((t) => eligibleTeacherIds.includes(String(t._id || t.id))),
    [allTeachers, eligibleTeacherIds]
  )
  const otherTeachers = useMemo(
    () => allTeachers.filter((t) => !eligibleTeacherIds.includes(String(t._id || t.id))),
    [allTeachers, eligibleTeacherIds]
  )
  const selectedOtherTeacher = useMemo(
    () => form.teacherId && !eligibleTeacherIds.includes(form.teacherId)
      ? allTeachers.find((t) => String(t._id || t.id) === form.teacherId) || null
      : null,
    [form.teacherId, eligibleTeacherIds, allTeachers]
  )
  const filteredOtherTeachers = useMemo(() => {
    const q = teacherSearch.toLowerCase()
    return otherTeachers.filter((t) => {
      const name = `${t.firstName || ''} ${t.lastName || ''}`.trim().toLowerCase()
      return !q || name.includes(q) || (t.email || '').toLowerCase().includes(q)
    })
  }, [otherTeachers, teacherSearch])

  const derivedDuration = useMemo(() => getDuration(form.startTime, form.endTime), [form.startTime, form.endTime])

  const visibleExams = useMemo(() => {
    const linked = exams.filter((e) => !!e?.examSessionId)
    if (sessionFilter !== 'all' || showOlderSessions) return linked
    return linked.filter(isExamInCurrentAcademicYearSession)
  }, [exams, sessionFilter, showOlderSessions])

  const examSections = useMemo(() => groupExamsBySessionAndClass(visibleExams), [visibleExams])

  // Auto-expand first session accordion
  useEffect(() => {
    if (!examSections.length) { setExpandedSessionKey(null); return }
    setExpandedSessionKey((curr) => curr && examSections.some((s) => s.key === curr) ? curr : examSections[0].key)
  }, [examSections])

  // Auto-expand first class group per session
  useEffect(() => {
    setExpandedClassBySession((curr) => {
      const next = {}
      examSections.forEach((section) => {
        const keys       = section.classGroups.map((g) => g.key)
        const currentKey = curr[section.key]
        next[section.key] = currentKey && keys.includes(currentKey) ? currentKey : keys[0] || null
      })
      const prevKeys = Object.keys(curr), nextKeys = Object.keys(next)
      return prevKeys.length === nextKeys.length && nextKeys.every((k) => curr[k] === next[k]) ? curr : next
    })
  }, [examSections])

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleToggleSession = (section) => {
    setExpandedSessionKey((curr) => curr === section.key ? null : section.key)
    setExpandedClassBySession((curr) => {
      if (curr[section.key] || !section.classGroups?.length) return curr
      return { ...curr, [section.key]: section.classGroups[0].key }
    })
  }

  const handleToggleClassGroup = (sectionKey, groupKey) => {
    setExpandedClassBySession((curr) => ({ ...curr, [sectionKey]: curr[sectionKey] === groupKey ? null : groupKey }))
  }

  const handleDelete = (exam) => {
    if (!window.confirm(`Remove "${exam.title}"?`)) return
    examsApi.remove(exam._id || exam.id)
      .then(() => { setExams((prev) => prev.filter((e) => (e._id || e.id) !== (exam._id || exam.id))); toast.success('Exam removed') })
      .catch(() => toast.error('Failed to remove exam'))
  }

  const handlePublishGroup = async (group, statsByExam) => {
    const examStats  = (group.data || []).map((exam) => ({ exam, stats: statsByExam[String(exam._id || exam.id)] }))
    const hasUnchecked = examStats.some(({ stats }) => !stats?.loaded)
    const hasMissing   = examStats.some(({ stats }) => !stats || stats.total === 0)
    const pending      = examStats.reduce((sum, { stats }) => sum + Math.max((stats?.total || 0) - (stats?.entered || 0), 0), 0)
    const unpublished  = examStats.filter(({ stats }) => stats && stats.total > 0 && stats.published < stats.total).map(({ exam }) => exam)

    if (hasUnchecked)  { toast.error('Still checking result sheets. Please wait.'); return }
    if (hasMissing)    { toast.error('Some exams have no students linked.'); return }
    if (pending > 0)   { toast.error(`${pending} result entr${pending === 1 ? 'y' : 'ies'} still pending.`); return }
    if (!unpublished.length) { toast('All results already published.'); return }

    if (!window.confirm(
      `Publish session results for ${group.title}?\n\nExams: ${unpublished.map((e) => e.title).join(', ')}`
    )) return

    try {
      setPublishingGroupKey(group.key)
      for (const exam of unpublished) {
        await examsApi.publishResults(exam._id || exam.id)
      }
      await fetchAll()
      toast.success(`Results published for ${group.title}`)
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to publish results')
    } finally {
      setPublishingGroupKey(null)
    }
  }

  const openAdd = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setSubjects([])
    setFormShowOlderSessions(false)
    setTeacherSearch('')
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Exam title is required'); return }
    if (!form.date)         { toast.error('Exam date is required');  return }
    try {
      setSaving(true)
      const payload = {
        ...form,
        totalMarks: Number(form.totalMarks) || 0,
        passMarks: Number(form.passMarks) || 0,
        duration: derivedDuration || undefined,
      }
      if (!payload.subjectId)     delete payload.subjectId
      if (!payload.classId)       delete payload.classId
      if (!payload.examSessionId) delete payload.examSessionId
      if (!payload.teacherId)     delete payload.teacherId
      if (!payload.startTime)     delete payload.startTime
      if (!payload.endTime)       delete payload.endTime
      if (!payload.venue)         delete payload.venue
      if (!payload.instructions)  delete payload.instructions
      if (editing) { await examsApi.update(editing, payload); toast.success('Exam updated') }
      else         { await examsApi.create(payload);          toast.success('Exam scheduled') }
      setModalOpen(false)
      await fetchAll()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save exam')
    } finally {
      setSaving(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      <PageHeader
        title="Exam Results"
        subtitle="Exams are grouped by session and class. Enter marks via Result Sheet, then publish session-wise when all marks are complete."
        chips={[loading ? 'Loading…' : `${exams.length} exams`]}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} className="btn-secondary p-2" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={openAdd} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Exam
            </button>
          </div>
        }
      />

      {/* Session filter chips */}
      <div className="flex flex-wrap gap-2">
        {sessionOptions.map((s) => {
          const active = sessionFilter === (s._id || s.id)
          return (
            <button
              key={s._id || s.id}
              onClick={() => setSessionFilter(s._id || s.id)}
              className={`text-xs font-semibold px-4 py-1.5 rounded-full border transition-colors ${
                active
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {s.name}
            </button>
          )
        })}
      </div>

      {/* Older sessions toggle */}
      {olderSessionCount > 0 && (
        <button
          onClick={() => setShowOlderSessions((prev) => !prev)}
          className="flex items-center gap-2 text-xs font-medium text-gray-500 border border-gray-200 rounded-xl px-3 py-2 hover:bg-gray-50 transition-colors"
        >
          {showOlderSessions
            ? <><Eye className="w-3.5 h-3.5" /> Hide older session years</>
            : <><Archive className="w-3.5 h-3.5" /> Show {olderSessionCount} older session year{olderSessionCount === 1 ? '' : 's'}</>
          }
        </button>
      )}

      {/* Class filter chips */}
      <div className="flex flex-wrap gap-2">
        {classOptions.map((c) => {
          const active = classFilter === (c._id || c.id)
          return (
            <button
              key={c._id || c.id}
              onClick={() => setClassFilter(c._id || c.id)}
              className={`text-xs font-semibold px-4 py-1.5 rounded-full border transition-colors ${
                active
                  ? 'bg-emerald-600 border-emerald-600 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {c.name}{c.section ? ` - ${c.section}` : ''}
            </button>
          )
        })}
      </div>

      {/* Exam sections */}
      {loading ? (
        <div className="card p-10 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-300 mx-auto" />
        </div>
      ) : !examSections.length ? (
        <div className="card p-10 flex flex-col items-center gap-3 text-center">
          <ClipboardList className="w-10 h-10 text-gray-200" />
          <p className="font-semibold text-gray-900">No exams found</p>
          <p className="text-sm text-gray-400">
            Add an exam and link it to a session to see it here.
          </p>
        </div>
      ) : (
        <div>
          {examSections.map((section) => (
            <SessionAccordion
              key={section.key}
              section={section}
              expanded={section.key === expandedSessionKey}
              onToggle={() => handleToggleSession(section)}
            >
              {section.classGroups.map((group) => (
                <ClassGroupAccordion
                  key={group.key}
                  group={group}
                  expanded={expandedClassBySession[section.key] === group.key}
                  onToggle={() => handleToggleClassGroup(section.key, group.key)}
                >
                  <SessionClassPublishCard
                    sectionTitle={section.title}
                    group={group}
                    publishing={publishingGroupKey === group.key}
                    onResultSheet={(examId) => navigate(`/exams/${examId}/result-sheet`)}
                    onDelete={handleDelete}
                    onPublishGroup={handlePublishGroup}
                  />
                </ClassGroupAccordion>
              ))}
            </SessionAccordion>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="font-bold text-gray-900">{editing ? 'Edit Exam' : 'Schedule Exam'}</h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-4">

              {/* ── Core Details ── */}
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 space-y-4">
                <p className="text-sm font-semibold text-gray-700">Core Details</p>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Exam Title *</label>
                  <input className="input-field w-full" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Mid-Term 2026" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Date *</label>
                  <input type="date" className="input-field w-full" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Start Time</label>
                    <input type="time" className="input-field w-full" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">End Time</label>
                    <input type="time" className="input-field w-full" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
                  </div>
                </div>
                {/* Duration panel */}
                <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-600">Duration</p>
                    {derivedDuration && (
                      <span className="text-[11px] font-semibold bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">
                        {derivedDuration} min
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">Auto-computed from start and end time.</p>
                  <div className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 ${derivedDuration ? 'border-primary-200 bg-primary-50/40' : 'border-gray-200 bg-gray-50'}`}>
                    <Plus className={`w-4 h-4 flex-shrink-0 ${derivedDuration ? 'text-primary-500' : 'text-gray-300'}`} />
                    <span className={`text-sm ${derivedDuration ? 'font-semibold text-primary-700' : 'text-gray-400'}`}>
                      {derivedDuration
                        ? `${Math.floor(derivedDuration / 60) > 0 ? `${Math.floor(derivedDuration / 60)}h ` : ''}${derivedDuration % 60 > 0 ? `${derivedDuration % 60}min` : ''} — ${form.startTime} to ${form.endTime}`
                        : 'Select start and end time'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Total Marks *</label>
                    <input type="number" className="input-field w-full" value={form.totalMarks} onChange={(e) => setForm({ ...form, totalMarks: e.target.value })} placeholder="100" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Pass Marks *</label>
                    <input type="number" className="input-field w-full" value={form.passMarks} onChange={(e) => setForm({ ...form, passMarks: e.target.value })} placeholder="35" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Venue</label>
                  <input className="input-field w-full" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="Room 101, Main Building" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Instructions</label>
                  <textarea rows={2} className="input-field w-full resize-none" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder="Bring blue pen, no calculator allowed…" />
                </div>
              </div>

              {/* ── Session ── */}
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-700">Session *</p>
                  {formOlderCount > 0 && (
                    <button type="button" onClick={() => setFormShowOlderSessions((p) => !p)} className="text-xs text-primary-600 font-medium">
                      {formShowOlderSessions ? 'Hide older' : `Show ${formOlderCount} older`}
                    </button>
                  )}
                </div>
                {!formShowOlderSessions && formOlderCount > 0 && (
                  <p className="text-xs text-gray-400">Showing {currentAcademicYear} sessions only.</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {formVisibleSessions.map((s) => {
                    const sid = s._id || s.id
                    const active = form.examSessionId === sid
                    return (
                      <button key={sid} type="button" onClick={() => setForm({ ...form, examSessionId: sid })}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border-[1.5px] transition-colors ${active ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                        {s.name} · {s.academicYear}
                      </button>
                    )
                  })}
                  {formVisibleSessions.length === 0 && <p className="text-xs text-gray-400">No sessions found. Create one first.</p>}
                </div>
                {form.examSessionId && (
                  <div className="flex items-start gap-2 rounded-xl border border-gray-200 bg-white p-3">
                    <Plus className="w-4 h-4 text-primary-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Results for this exam will be published session-wise. Admin publishes all class results for the session together after all result sheets are complete.
                    </p>
                  </div>
                )}
              </div>

              {/* ── Class & Subject ── */}
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700">Class & Subject</p>
                <div className="flex flex-wrap gap-2">
                  {classes.map((c) => {
                    const cid = c._id || c.id
                    const active = form.classId === cid
                    return (
                      <button key={cid} type="button"
                        onClick={() => setForm({ ...form, classId: cid, subjectId: '', teacherId: '' })}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border-[1.5px] transition-colors ${active ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                        {c.name}{c.section ? ` - ${c.section}` : ''}
                      </button>
                    )
                  })}
                </div>
                {form.classId && (
                  <>
                    <p className="text-xs font-semibold text-gray-500 mt-1">
                      Subjects for {classes.find((c) => (c._id || c.id) === form.classId)?.name || 'class'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {subjects.length === 0
                        ? <p className="text-xs text-gray-400">No subjects loaded for this class.</p>
                        : subjects.map((s) => {
                          const sid = s._id || s.id
                          const active = form.subjectId === sid
                          return (
                            <button key={sid} type="button"
                              onClick={() => setForm({ ...form, subjectId: sid, teacherId: '' })}
                              className={`text-xs font-medium px-3 py-1.5 rounded-full border-[1.5px] transition-colors ${active ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                              {s.name}
                            </button>
                          )
                        })}
                    </div>
                  </>
                )}
              </div>

              {/* ── Assign Teacher ── */}
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 space-y-3">
                <p className="text-sm font-semibold text-gray-700">Assign Teacher *</p>
                {form.classId && form.subjectId ? (
                  <>
                    {eligibleTeachers.length > 0 && (
                      <>
                        <p className="text-xs font-semibold text-primary-600">Eligible (teach this subject)</p>
                        <div className="flex flex-wrap gap-2">
                          {eligibleTeachers.map((t) => {
                            const tid = String(t._id || t.id)
                            const active = form.teacherId === tid
                            const name = `${t.firstName || ''} ${t.lastName || ''}`.trim() || t.name || 'Teacher'
                            return (
                              <button key={tid} type="button" onClick={() => setForm({ ...form, teacherId: active ? '' : tid })}
                                className={`text-xs font-semibold px-3 py-1.5 rounded-full border-[1.5px] transition-colors ${active ? 'border-primary-500 bg-primary-600 text-white' : 'border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100'}`}>
                                {name}
                              </button>
                            )
                          })}
                        </div>
                        <div className="h-px bg-gray-200" />
                      </>
                    )}
                    <p className="text-xs font-semibold text-gray-400">Other Teachers</p>
                  </>
                ) : (
                  <p className="text-xs text-gray-400">Select class and subject first to load eligible teachers.</p>
                )}
                {/* Selected other teacher badge */}
                {selectedOtherTeacher && (
                  <div className="flex items-center gap-3 rounded-xl border border-cyan-200 bg-cyan-50 p-3">
                    <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-white">
                        {(`${selectedOtherTeacher.firstName || ''} ${selectedOtherTeacher.lastName || ''}`.trim() || 'T')[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-cyan-800">{`${selectedOtherTeacher.firstName || ''} ${selectedOtherTeacher.lastName || ''}`.trim()}</p>
                      {selectedOtherTeacher.email && <p className="text-xs text-cyan-600">{selectedOtherTeacher.email}</p>}
                    </div>
                    <button onClick={() => setForm({ ...form, teacherId: '' })} className="text-cyan-500 hover:text-cyan-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <button type="button" onClick={() => { setTeacherSearch(''); setShowTeacherModal(true) }}
                  className="w-full flex items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-white px-4 py-3 text-sm text-gray-500 hover:border-gray-400 transition-colors">
                  <Plus className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm">{selectedOtherTeacher ? 'Change other teacher' : 'Select other teacher…'}</span>
                </button>
              </div>

              {/* ── Exam Type & Status ── */}
              <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4 space-y-4">
                <p className="text-sm font-semibold text-gray-700">Exam Type</p>
                <div className="flex flex-wrap gap-2">
                  {EXAM_TYPES.map((t) => {
                    const active = form.examType === t
                    return (
                      <button key={t} type="button" onClick={() => setForm({ ...form, examType: t })}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border-[1.5px] capitalize transition-colors ${active ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                        {EXAM_TYPE_LABELS[t] || t}
                      </button>
                    )
                  })}
                </div>
                <p className="text-sm font-semibold text-gray-700">Exam Status</p>
                <div className="flex flex-wrap gap-2">
                  {EXAM_STATUSES.map((s) => {
                    const active = form.status === s
                    return (
                      <button key={s} type="button" onClick={() => setForm({ ...form, status: s })}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border-[1.5px] capitalize transition-colors ${active ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                        {EXAM_STATUS_LABELS[s] || s}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 flex gap-3 justify-end border-t border-gray-100 flex-shrink-0">
              <button onClick={() => setModalOpen(false)} className="btn-secondary" disabled={saving}>Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? 'Update' : 'Schedule Exam'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teacher picker modal */}
      {showTeacherModal && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <h4 className="font-bold text-gray-900">Other Teachers</h4>
              <button onClick={() => setShowTeacherModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  autoFocus
                  value={teacherSearch}
                  onChange={(e) => setTeacherSearch(e.target.value)}
                  placeholder="Search teacher by name or email"
                  className="w-full bg-transparent text-sm outline-none"
                />
                {teacherSearch && (
                  <button onClick={() => setTeacherSearch('')} className="text-gray-400 hover:text-gray-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {filteredOtherTeachers.length === 0 ? (
                <div className="py-10 text-center text-gray-400 text-sm">No teachers found</div>
              ) : filteredOtherTeachers.map((t) => {
                const tid = String(t._id || t.id)
                const active = form.teacherId === tid
                const name = `${t.firstName || ''} ${t.lastName || ''}`.trim() || t.name || 'Teacher'
                const initial = name[0]?.toUpperCase() || 'T'
                return (
                  <button
                    key={tid}
                    type="button"
                    onClick={() => { setForm((p) => ({ ...p, teacherId: tid })); setShowTeacherModal(false) }}
                    className={`w-full flex items-center gap-3 px-5 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors text-left ${active ? 'bg-cyan-50' : ''}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${active ? 'bg-cyan-600' : 'bg-gray-300'}`}>
                      <span className="text-sm font-bold text-white">{initial}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${active ? 'text-cyan-800' : 'text-gray-900'}`}>{name}</p>
                      {t.email && <p className="text-xs text-gray-400 truncate">{t.email}</p>}
                    </div>
                    {active && <Plus className="w-4 h-4 text-cyan-600 flex-shrink-0 rotate-45" />}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
